import { Component, Input, OnInit } from '@angular/core';
import { MetaData } from '@nbg-globals/urlbuilder';
import { LoginAction, ActionBtnId } from '../../classes/static/enums';
import { ErrorHandler } from '../../../globals/classes/errorHandler';
import { IErrorDescription } from '@ibank-globals/error';
import { ActionBtn } from '../../classes/action-btn';
import { ISignInErrorInfo } from '../../classes/sign-in-error';
import { LinkUsersService } from '../../services/link-user.service';
import { Extensions } from '../../classes/static/extensions';
import { withLatestFrom, map } from 'rxjs/operators';
import { LoginService } from '../../services/login.service';
import { BaseComponent } from '../../../globals/classes/base.component';
import { UserData } from '../../../globals/classes/userData';

@Component({
	selector: 'sign-in',
	template: require('./sign-in.component.html'),
})
export class SignInComponent extends BaseComponent implements OnInit {
	@Input() public action: LoginAction;

	private _metaReader = new MetaData();
	public errorInfo: ISignInErrorInfo;

	ngOnInit(): void {
		this.setupSubscriptions();
		var isUnderMaintenance = 'true' === this._metaReader.get('mntc').toLowerCase();
		if (isUnderMaintenance) {
			this.errorInfo = {
				messageParts: [this._metaReader.get('mntcMsg')],
				title: 'Σημαντική ενημέρωση',
			};
		}
	}

	public errorActionClicked(action: ActionBtn): void {
		switch (action.id) {
			case ActionBtnId.NavigateToOldSite:
				window.location.href = 'https://ibankretail.nbg.gr/sts/Account/Login/web';
				break;
			case ActionBtnId.Retry:
				this.clearError();
				break;
			case ActionBtnId.ReturnToClient:
				window.location.href = Extensions.returnUrl;
				break;
		}
	}

	private clearError() {
		this.errorInfo = null;
	}

	private setupSubscriptions(): void {
		this.mortalize(ErrorHandler.errorObs)
			.pipe(
				withLatestFrom(
					LoginService.loginAction$,
					UserData.data$.pipe(map((info) => (!!info ? info.userId : '')))
				),
				map((data) => this.handleAppError(data[0], data[1], data[2]))
			)
			.subscribe();
	}

	private readonly returnToClientAction: ActionBtn = {
		id: ActionBtnId.ReturnToClient,
		text: 'Επιστροφή',
	};

	private readonly retryActionBtn: ActionBtn = {
		id: ActionBtnId.Retry,
		text: 'Νέα σύνδεση',
	};

	private getAlreadyLinkedMsg(username: string): ISignInErrorInfo {
		return {
			messageParts: [
				'Η σύνδεση του ομιλικού προφίλ',
				username,
				'δεν ήταν εφικτή. Ήδη συνδεδεμένος χρήστης. Παρακαλούμε δοκιμάστε διαφορετικό προφίλ',
			],
			action: this.retryActionBtn,
		};
	}

	private getLinkNotFoundMsg(username: string): ISignInErrorInfo {
		return {
			messageParts: [
				'Η μετάβαση στο ομιλικό προφίλ',
				username,
				'δεν ήταν εφικτή. Παρακαλούμε δοκιμάστε διαφορετικό προφίλ',
			],
			action: this.returnToClientAction,
		};
	}

	private getInvalidIbankUserIdFailMessage(
		username: string,
		loginAction: LoginAction
	): ISignInErrorInfo {
		let msgPart1 = '';
		switch (loginAction) {
			case LoginAction.LinkUsers:
				msgPart1 = 'Η σύνδεση του προφίλ';
				break;
			case LoginAction.RenewLink:
				msgPart1 = 'Η ανανέωση του προφίλ';
				break;
			case LoginAction.SwitchUser:
				msgPart1 = 'Η αλλαγή στο προφίλ';
				break;
		}
		return {
			messageParts: [
				msgPart1,
				username,
				'δεν ήταν εφικτή. Παρακαλούμε επιβεβαιώστε ότι το όνομα χρήστη είναι ορθό και ανήκει στον όμιλό σας. Αν αυτά ισχύουν, παρακαλούμε δοκιμάστε ξανά αργότερα. Ευχαριστούμε για την κατανόηση',
			],
			action:
				loginAction === LoginAction.LinkUsers
					? this.retryActionBtn
					: this.returnToClientAction,
		};
	}

	private getUnknownLinkFailMessage(
		username: string,
		loginAction: LoginAction
	): ISignInErrorInfo {
		let msgPart1 = '';
		switch (loginAction) {
			case LoginAction.LinkUsers:
				msgPart1 = 'Η σύνδεση του ομιλικού προφίλ';
				break;
			case LoginAction.RenewLink:
				msgPart1 = 'Η ανανέωση του ομιλικού προφίλ';
				break;
			case LoginAction.SwitchUser:
				msgPart1 = 'Η αλλαγή στο ομιλικό προφίλ';
				break;
		}
		return {
			messageParts: [
				msgPart1,
				username,
				'δεν ήταν εφικτή. Παρακαλούμε δοκιμάστε ξανά αργότερα. Ευχαριστούμε για την κατανόηση',
			],
			action:
				loginAction === LoginAction.LinkUsers
					? this.retryActionBtn
					: this.returnToClientAction,
		};
	}

	private handleAppError(
		error: IErrorDescription,
		loginAction: LoginAction,
		username: string
	): void {
		if (!error) return;
		var errorCode = parseInt(error.code);
		if (!Extensions.isLinkUserErrorCode(errorCode)) return;

		switch (errorCode) {
			case 4016:
			case 4017:
				this.errorInfo = this.getAlreadyLinkedMsg(username);
				break;
			case 4018:
				this.errorInfo = this.getLinkNotFoundMsg(username);
				break;
			case 4019:
				this.errorInfo = this.getInvalidIbankUserIdFailMessage(username, loginAction);
				break;
			default:
				this.errorInfo = this.getUnknownLinkFailMessage(username, loginAction);
		}
	}
}
